import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import React from 'react';
import { Box, Card, Flex, Heading, Icon, Text } from 'roo-ui/components';
import { themeGet } from 'styled-system';
import Image from 'components/Image';
import PointsEarnSummary from 'components/PointsEarnSummary';
import Truncate from 'components/Truncate';
import PromotionalSash from 'components/PromotionalSash';
import PropertyRatings from './PropertyRatings';
import OfferBreakdown from './OfferBreakdown';
import CampaignPriceMessage from 'components/CampaignPriceMessage';
import AppLink from 'components/AppLink';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { getPropertyLinkQuery } from 'store/search/searchSelectors';
import createSearchQueryString from 'lib/search/createSearchQueryString';
import { useSelector } from 'react-redux';

const StyledCard = styled(Card)`
  width: 100%;
  background-color: #fff;
  box-shadow: ${themeGet('shadows.hard')};
  padding: 0;
  overflow: hidden;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: ${themeGet('shadows.hover')};
  }
`;

const PropertyName = styled(Heading.h4)`
  color: ${themeGet('colors.greys.charcoal')};
  font-size: ${themeGet('fontSizes.md')};
  margin-bottom: 0;
`;

const NakedAppLink = styled(AppLink)`
  display: block;
  text-decoration: none;
`;

const propTypes = {
  compact: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  imageSrc: PropTypes.string.isRequired,
  imageSrcSet: PropTypes.string,
  imageAltTag: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  promotionName: PropTypes.string,
  target: PropTypes.string,
  to: PropTypes.string.isRequired,
};

const defaultProps = {
  imageSrcSet: undefined,
  onClick: undefined,
  promotionName: undefined,
  target: undefined,
};

const InlineCard = ({ compact, children, imageSrc, imageSrcSet, imageAltTag, promotionName, target, to, onClick, ...rest }) => {
  const theme = useTheme();
  const imageSize = compact ? { ...theme.imageSizes.mapSearchResultPhone } : { ...theme.imageSizes.mapSearchResult };

  return (
    <StyledCard as={NakedAppLink} to={to} rel="noopener" target={target} onClick={onClick} {...rest} data-testid="inline-card">
      <Flex flexDirection="row" height="100%">
        <Box width={compact ? 96 : 135}>
          <Image src={imageSrc} srcSet={imageSrcSet} alt={imageAltTag} {...imageSize} />
        </Box>

        <Flex flexDirection="column" justifyContent="space-between" p={3} pt={promotionName ? 0 : 3} width={1}>
          {promotionName && <PromotionalSash promotionName={promotionName} />}
          {children}
        </Flex>
      </Flex>
    </StyledCard>
  );
};

InlineCard.propTypes = propTypes;
InlineCard.defaultProps = defaultProps;

const StackedCard = ({ children, imageSrc, imageSrcSet, imageAltTag, promotionName, target, to, onClick, ...rest }) => (
  <StyledCard as={NakedAppLink} to={to} rel="noopener" target={target} onClick={onClick} width={300} {...rest} data-testid="stacked-card">
    <Flex flexDirection="column" justifyContent="space-between">
      {promotionName && (
        <Box position="absolute" ml={3}>
          <PromotionalSash promotionName={promotionName} />
        </Box>
      )}
      <Box height={120}>
        <Image lazy src={imageSrc} srcSet={imageSrcSet} alt={imageAltTag} height={120} width={1} />
      </Box>
      <Box m={3}>{children}</Box>
    </Flex>
  </StyledCard>
);

StackedCard.propTypes = propTypes;
StackedCard.defaultProps = defaultProps;

const PropertyCard = React.memo(
  ({
    id: propertyId,
    inline,
    propertyName,
    rating: starRating,
    ratingType: starRatingType,
    customerRating,
    total,
    hasDiscount,
    totalBeforeDiscount,
    isNonRefundable,
    pointsEarned,
    offerType,
    imageSrc,
    imageSrcSet,
    imageAltTag,
    promotionName,
    onClick,
    showCampaignMessage,
    openPropertyInNewTab,
    featuredOfferId,
    country,
    ...rest
  }) => {
    if (!propertyId) return null;

    const { isLessThanBreakpoint } = useBreakpoints();
    const isMobile = isLessThanBreakpoint(0);

    const propertyLinkQuery = useSelector(getPropertyLinkQuery);
    const updatedPropertyLinkQuery = {
      ...propertyLinkQuery,
      ...(featuredOfferId && {
        featuredOfferId,
        payWith: offerType === 'classic' ? 'points' : 'cash',
      }),
    };
    const propertyLinkQueryString = createSearchQueryString(updatedPropertyLinkQuery);
    const propertyUrl = `/properties/${propertyId}?${propertyLinkQueryString}`;

    const Component = inline ? InlineCard : StackedCard;
    const currency = total.currency;

    return (
      <Component
        compact={isMobile}
        imageAltTag={imageAltTag}
        imageSrc={imageSrc}
        imageSrcSet={imageSrcSet}
        onClick={onClick}
        promotionName={promotionName}
        target={openPropertyInNewTab ? '_blank' : undefined}
        to={propertyUrl}
        {...rest}
      >
        <Truncate textAlign="left" lines={2} mt={1}>
          <PropertyName lineHeight="1.25">{propertyName}</PropertyName>
        </Truncate>
        <PropertyRatings starRating={starRating} starRatingType={starRatingType} customerRating={customerRating} />
        <OfferBreakdown hasDiscount={hasDiscount} offerType={offerType} total={total} totalBeforeDiscount={totalBeforeDiscount} />

        <Flex alignItems="flex-end" flexDirection="column">
          {showCampaignMessage && <CampaignPriceMessage currency={currency} offerType={offerType} country={country} />}

          <PointsEarnSummary {...pointsEarned.qffPoints} />

          {!isNonRefundable && (
            <Flex alignItems="center">
              <Icon color="green" mt={1} mr={2} size={22} name="freeCancellation" />
              <Text color="green" display="block" fontWeight="bold" textDecoration="underline" data-testid="free-cancellation-text">
                Free cancellation
              </Text>
            </Flex>
          )}
        </Flex>
      </Component>
    );
  },
);

PropertyCard.propTypes = {
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  inline: PropTypes.bool,
  propertyName: PropTypes.string,
  propertyUrl: PropTypes.string,
  rating: PropTypes.number,
  ratingType: PropTypes.string,
  customerRating: PropTypes.object,
  total: PropTypes.object,
  isNonRefundable: PropTypes.bool,
  pointsEarned: PropTypes.shape({
    qffPoints: PropTypes.shape({
      total: PropTypes.number,
      base: PropTypes.number,
    }),
  }),
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  imageSrc: PropTypes.string,
  imageSrcSet: PropTypes.string,
  imageAltTag: PropTypes.string,
  promotionName: PropTypes.string,
  hasDiscount: PropTypes.bool,
  totalBeforeDiscount: PropTypes.object,
  offerType: PropTypes.string,
  onClick: PropTypes.func,
  showCampaignMessage: PropTypes.bool,
  openPropertyInNewTab: PropTypes.bool,
  featuredOfferId: PropTypes.string,
  country: PropTypes.string,
};

PropertyCard.defaultProps = {
  id: undefined,
  inline: undefined,
  customerRating: {},
  isNonRefundable: undefined,
  latitude: undefined,
  longitude: undefined,
  imageSrcSet: undefined,
  promotionName: '',
  hasDiscount: undefined,
  totalBeforeDiscount: {},
  offerType: undefined,
  propertyName: undefined,
  propertyUrl: undefined,
  rating: undefined,
  ratingType: undefined,
  total: {},
  pointsEarned: {},
  imageSrc: undefined,
  imageAltTag: undefined,
  onClick: undefined,
  showCampaignMessage: false,
  openPropertyInNewTab: false,
  featuredOfferId: undefined,
  country: undefined,
};

PropertyCard.displayName = 'PropertyCard';

export default PropertyCard;
